import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import ColorSwatchPicker from '~components/FormFields/ColorSwatchPicker';
import TextInput from '~components/FormFields/v2/TextInput';
import LabelChip from '~components/Labels/LabelChip';
import { Label } from '~hooks/useLabels/models';
import { DEFAULT_PALETTE_COLOR } from '~utils/colors';

import {
  LabelDto,
  LabelDtoAsyncError,
  labelSchema,
  setLabelSchemaDefaultValues,
} from './labelSchema';

export type LabelFormHandler = {
  onSubmit: () => Promise<LabelDto | undefined>;
};

interface LabelFormProps {
  asyncErrors?: LabelDtoAsyncError[];
  label?: Label;
}

const LabelForm = forwardRef<LabelFormHandler, LabelFormProps>(function LabelForm(
  { asyncErrors, label },
  ref,
) {
  const { handleSubmit, control, setError, clearErrors } = useForm<LabelDto>({
    resolver: yupResolver(labelSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setLabelSchemaDefaultValues(label),
  });

  const watchedName = useWatch({ control, name: 'name' });
  const watchedColor = useWatch({ control, name: 'color' });

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    if (asyncErrors) {
      for (const error of asyncErrors) {
        setError(error.field as keyof LabelDto, {
          message: error.message,
        });
      }
    }

    return () => {
      clearErrors();
    };
  }, [asyncErrors]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 2,
      }}
    >
      <Box>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.name')}
          name="name"
        />
      </Box>

      <Box>
        <Box sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
          {t('form_fields.color')}
        </Box>
        <ColorSwatchPicker control={control} name="color" />
      </Box>

      <Box sx={{ mt: 1 }}>
        <Box sx={{ mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
          {t('common.preview')}
        </Box>
        <LabelChip
          name={watchedName || t('form_fields.name')}
          color={watchedColor || DEFAULT_PALETTE_COLOR}
        />
      </Box>
    </Box>
  );
});

export default LabelForm;
